import { createElement } from '@wordpress/element';

// Sanitize badge text — allow <br> only
const sanitize = (t = '') =>
    t.replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&amp;/gi, '&')
     .replace(/\r?\n/g, '<br>')
     .replace(/<(?!br\s*\/?>)[^>]+>/gi, '')
     .replace(/script|javascript|onerror|onload|onclick/gi, '');

// Map badge position + custom offsets to absolute CSS
const posStyle = (pos, top, left, right, bottom) => {
    const px = (v, fallback = 0) => `${v !== '' && v !== undefined ? v : fallback}px`;
    switch (pos) {
        case 'top_left':        return { top: 0,    left: 0    };
        case 'top_right':       return { top: 0,    right: 0   };
        case 'bottom_left':     return { bottom: 0, left: 0    };
        case 'bottom_right':    return { bottom: 0, right: 0   };
        case 'custom_position': return {
            top:    top    !== '' ? px(top)    : undefined,
            left:   left   !== '' && (!right || right === '0') ? px(left)   : undefined,
            right:  right  !== '' && right !== '0' ? px(right)  : undefined,
            bottom: bottom !== '' && bottom !== '0' ? px(bottom) : undefined,
        };
        default: return { top: 0, left: 0 };
    }
};

// Shape → CSS dimensions and border-radius
const SHAPE_CSS = {
    square:           { width: 58, height: 58, borderRadius: 2 },
    rectangle:        { width: 90, height: 42, borderRadius: 0 },
    rectangle_up:     { width: 52, height: 52, borderRadius: 4, transform: 'rotate(45deg)', opacity: 0.7 },
    // offers:           { width: 56, height: 56, borderRadius: 0, clipPath: 'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)' },
    offers: { width: 62, height: 62, borderRadius: 0, clipPath: 'polygon(50% 0%,60% 13%,75% 7%,77% 23%,93% 25%,87% 40%,100% 50%,87% 60%,93% 75%,77% 77%,75% 93%,60% 87%,50% 100%,40% 87%,25% 93%,23% 77%,7% 75%,13% 60%,0% 50%,13% 40%,7% 25%,23% 23%,25% 7%,40% 13%)' },
    // tag:              { width: 54, height: 42, clipPath: 'polygon(0 0,100% 0,100% 72%,50% 100%,0 72%)' },
    tag: { width: 40, height: 58, clipPath: 'polygon(0% 0%,100% 0%,100% 100%,50% 75%,0% 100%)' },
    collar:           { width: 52, height: 70, borderRadius: 0 },
    rectangle_round:  { width: 86, height: 38, borderRadius: 5 },
    rectangle_circle: { width: 78, height: 42, borderRadius: '50%' },
    circle:           { width: 54, height: 54, borderRadius: '50%' },
    // corner_badge:     { width: 60, height: 60, borderRadius: 0, transform: 'rotate(45deg)', overflow: 'hidden' },
    corner_badge: { width: 80, height: 80, borderRadius: 0 },
};

const BadgePreview = ({ form, assetsUrl }) => {
    const {
        pbfw_show_label,
        badge_define,
        pbfw_lbl_shape,
        pbfw_discount_text,
        pbfw_font_clr,
        pbfw_bg_clr,
        pbfw_ft_size,
        pbfw_font_weight,
        pbfw_image_position,
        pbfw_top,
        pbfw_left,
        pbfw_right,
        pbfw_bottom,
        pbfw_background,
        image_badge,
        ocor_bg_img_form_url,
    } = form;

    const isVisible  = pbfw_show_label !== 'off';
    const isText     = badge_define !== 'or_image_badge';
    const shape      = pbfw_lbl_shape || 'square';
    const shapeCSS   = SHAPE_CSS[shape] || SHAPE_CSS.square;

    let finalShapeCSS = shapeCSS;
    if (shape === 'corner_badge') {
        const cornerClips = {
            top_left:     'polygon(0% 0%, 100% 0%, 0% 100%)',
            top_right:    'polygon(0% 0%, 100% 0%, 100% 100%)',
            bottom_left:  'polygon(0% 0%, 0% 100%, 100% 100%)',
            bottom_right: 'polygon(100% 0%, 0% 100%, 100% 100%)',
        };
        let clipKey = pbfw_image_position;
        if (pbfw_image_position === 'custom_position') {
            const r = parseFloat(pbfw_right)  || 0;
            const b = parseFloat(pbfw_bottom) || 0;
            if      ( b > 0 &&  r > 0) clipKey = 'bottom_right';
            else if ( b > 0 && !r)     clipKey = 'bottom_left';
            else if (!b      &&  r > 0) clipKey = 'top_right';
            else                        clipKey = 'top_left';
        }
        finalShapeCSS = { ...shapeCSS, clipPath: cornerClips[clipKey] || cornerClips.top_left };
    }

    // Correct arg order: top, left, right, bottom (matching the posStyle signature)
    const pos = posStyle(
        pbfw_image_position,
        pbfw_top,
        pbfw_left,
        pbfw_right,
        pbfw_bottom
    );

    const badgeText  = sanitize(pbfw_discount_text || 'Sale');
    const fontColor  = pbfw_font_clr  || '#ffffff';
    const bgColor    = pbfw_bg_clr    || '#6366f1';
    const fontSize   = Math.max(8, parseInt(pbfw_ft_size, 10) || 14);
    const fontWeight = pbfw_font_weight || 'bold';

    const textStyle = {
        ...finalShapeCSS,
        backgroundColor: bgColor,
        color:           fontColor,
        display:         'inline-flex',
        // alignItems:      'center',
        alignItems:
            shape === 'corner_badge'
                ? (pbfw_image_position === 'top_left' || pbfw_image_position === 'top_right'
                    ? 'flex-start'
                    : 'flex-end')
                : 'center',
        justifyContent:  'center',
        fontSize,
        fontWeight,
        position:        'absolute',
        lineHeight:      1.2,
        // padding:         '4px 6px',
        padding:
            shape === 'corner_badge'
                    ? '9px 9px'
                    : '4px 6px',
        boxSizing:       'border-box',
        wordBreak:       'break-word',
        textAlign:       'center',
        ...pos,
    };

    const imageSrc = pbfw_background === 'or_badge_image_upload'
        ? (ocor_bg_img_form_url || '')
        : `${assetsUrl}/img/${image_badge || 'badge.png'}`;

    return (
        <div className="pbfw-preview-card cpiw-card">
            <div className="cpiw-card__header">
                <div className="cpiw-card__header-icon">
                    <span style={{ fontSize: 15 }}>👁</span>
                </div>
                <div>
                    <div className="cpiw-card__title">Live Preview</div>
                    <div className="cpiw-card__desc">Updates as you type</div>
                </div>
            </div>

            <div className="cpiw-card__body">
                {/* Product image with badge overlay */}
                <div className="pbfw-preview-wrap">
                    <img
                        src={`${assetsUrl}/img/preview-product.jpg`}
                        alt="Product"
                        className="pbfw-preview-img"
                    />

                    {isVisible && (
                        <div className="pbfw-badge-overlay">
                            {isText ? (
    shape === 'collar' ? (
        <div style={{ position: 'absolute', width: 52, height: 70, ...pos }}>
            {/* Circle with text */}
            <div style={{
                width: 52, height: 52, borderRadius: '50%',
                backgroundColor: bgColor, color: fontColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize, fontWeight, textAlign: 'center',
                padding: '6px', boxSizing: 'border-box',
                position: 'relative', zIndex: 1,
            }}>
                <span dangerouslySetInnerHTML={{ __html: badgeText }} />
            </div>
            {/* Left tail */}
            <div style={{
                position: 'absolute', bottom: 6, left: 4,
                width: 29, height: 22,
                backgroundColor: bgColor,
                clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
                transform: 'rotate(34deg)', transformOrigin: 'top center',
            }} />
            {/* Right tail */}
            <div style={{
                position: 'absolute', bottom: 6, right: 4,
                width: 29, height: 22,
                backgroundColor: bgColor,
                clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
                transform: 'rotate(-34deg)', transformOrigin: 'top center',
            }} />
        </div>
    ) : (
    <div style={textStyle}>
        <span
            dangerouslySetInnerHTML={{ __html: badgeText }}
            style={{
                ...(shape === 'corner_badge' && (() => {
                    let cornerTop, cornerLeft, cornerRot;
                    if (pbfw_image_position === 'custom_position') {
                        const r = parseFloat(pbfw_right)  || 0;
                        const b = parseFloat(pbfw_bottom) || 0;
                        const isRight  = r > 0;
                        const isBottom = b > 0;
                        cornerTop  = isBottom ? '65%' : '35%';
                        cornerLeft = isRight  ? '65%' : '35%';
                        cornerRot  = (isBottom === isRight) ? 'rotate(-45deg)' : 'rotate(45deg)';
                    } else {
                        cornerTop  = (pbfw_image_position === 'top_left'    || pbfw_image_position === 'top_right')    ? '35%' : '65%';
                        cornerLeft = (pbfw_image_position === 'top_left'    || pbfw_image_position === 'bottom_left')  ? '35%' : '65%';
                        cornerRot  = {
                            top_left:     'rotate(-45deg)',
                            top_right:    'rotate(45deg)',
                            bottom_left:  'rotate(45deg)',
                            bottom_right: 'rotate(-45deg)',
                        }[pbfw_image_position] || 'rotate(-45deg)';
                    }
                    return {
                        position:  'absolute',
                        top:       cornerTop,
                        left:      cornerLeft,
                        transform: `translate(-50%, -50%) ${cornerRot}`,
                        display:   'block',
                        textAlign: 'center',
                        width:     '70%',
                    };
                })()),
                ...(shape === 'rectangle_up' && {
                  transform: 'rotate(-45deg)',
                  display: 'inline-block',
                  width: '100%',
                  textAlign: 'center',
                }),
              }}
        />
    </div>
    )
                            ) : (
                                imageSrc ? (
                                    <div style={{ position: 'absolute', width: 64, height: 64, ...pos }}>
                                        <img
                                            src={imageSrc}
                                            alt="Badge"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        position: 'absolute', ...pos,
                                        background: 'rgba(0,0,0,.1)',
                                        width: 64, height: 64,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 10, color: '#888', borderRadius: 4, border: '1px dashed #ccc',
                                    }}>No image</div>
                                )
                            )}
                        </div>
                    )}

                    {!isVisible && (
                        <div className="pbfw-preview-hidden">Badge hidden</div>
                    )}
                </div>

                {/* Meta summary */}
                <div className="pbfw-preview-meta">
                    <div className="pbfw-preview-meta-row">
                        <span>Type</span>
                        <strong>{isText ? '🔤 Text' : '🖼 Image'}</strong>
                    </div>
                    {isText && (
                        <>
                            <div className="pbfw-preview-meta-row">
                                <span>Shape</span>
                                <strong style={{ textTransform: 'capitalize' }}>
                                    {shape.replace(/_/g, ' ')}
                                </strong>
                            </div>
                            <div className="pbfw-preview-meta-row">
                                <span>Colours</span>
                                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span title={`Background: ${bgColor}`} style={{
                                        width: 16, height: 16, borderRadius: 3,
                                        background: bgColor,
                                        border: '1px solid rgba(0,0,0,.15)',
                                        display: 'inline-block',
                                    }} />
                                    <span title={`Text: ${fontColor}`} style={{
                                        width: 16, height: 16, borderRadius: 3,
                                        background: fontColor,
                                        border: '1px solid rgba(0,0,0,.15)',
                                        display: 'inline-block',
                                    }} />
                                </span>
                            </div>
                            <div className="pbfw-preview-meta-row">
                                <span>Font size</span>
                                <strong>{fontSize}px</strong>
                            </div>
                        </>
                    )}
                    <div className="pbfw-preview-meta-row">
                        <span>Position</span>
                        <strong style={{ textTransform: 'capitalize' }}>
                            {(pbfw_image_position || 'top_left').replace(/_/g, ' ')}
                        </strong>
                    </div>
                    <div className="pbfw-preview-meta-row">
                        <span>Visible</span>
                        <strong style={{ color: isVisible ? 'var(--cpiw-accent)' : 'var(--cpiw-danger)' }}>
                            {isVisible ? '✓ Yes' : '✕ No'}
                        </strong>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BadgePreview;
